import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import type { MetroStop } from "../../../utils/metro-stops";

import { KataRobot } from "../../logo/KataRobot";

export type CourseMetroHeaderProps = {
    title: string;
    cover: string;
    stops: MetroStop[];
    onStart: (stop: MetroStop) => void;
};

/** The course at a glance: how far the required lessons got, and what to take next. */
export function CourseMetroHeader({ title, cover, stops, onStart }: CourseMetroHeaderProps) {
    const counted = stops.filter((stop) => stop.kind !== 'off');
    const required = counted.filter((stop) => !stop.bonus);
    const bonus = counted.filter((stop) => stop.bonus);
    const finished = required.filter((stop) => stop.kind === 'done').length;
    const started = required.filter((stop) => stop.kind === 'recap' || stop.kind === 'waiting').length;
    const share = (count: number) => `${ required.length ? count / required.length * 100 : 0 }%`;

    return (
        <Stack spacing={ 3 }>
            <Stack component="nav" aria-label="Breadcrumb" direction="row" spacing={ 1 } sx={ { fontSize: 13, color: '#4F5B67' } }>
                <Link component={ RouterLink } to="/courses" underline="hover">Courses</Link>
                <span aria-hidden="true">/</span>
                <span>{ title }</span>
            </Stack>

            <Stack direction={ { xs: 'column', md: 'row' } } spacing={ 2.5 } sx={ {
                bgcolor: '#FFFFFF', border: '1px solid #E3E8EE', borderRadius: '20px', p: 1.75,
            } }>
                <Box component="img" src={ cover } alt="" sx={ {
                    width: { xs: '100%', md: 200 }, height: { xs: 160, md: 150 }, objectFit: 'cover', borderRadius: '12px', flexShrink: 0,
                } }/>

                <Stack justifyContent="center" spacing={ 1.25 } sx={ { flexGrow: 1, minWidth: 0 } }>
                    <Box>
                        <Typography component="h1" variant="inherit" sx={ { fontSize: 28, lineHeight: '34px', fontWeight: 800, letterSpacing: '-0.02em' } }>
                            { title }
                        </Typography>
                        <Typography variant="body2" sx={ { fontWeight: 700, color: '#4F5B67' } }>
                            { stops.length } lessons · { new Set(stops.map((stop) => stop.lesson.groupId)).size } units
                        </Typography>
                    </Box>

                    <Stack spacing={ 0.75 }>
                        <Stack direction="row" justifyContent="space-between" sx={ { fontSize: 13 } }>
                            <Box component="span" sx={ { fontWeight: 700 } }>{ finished } of { required.length } required lessons finished</Box>
                            <Box component="span" sx={ { color: '#4F5B67' } }>
                                Bonus { bonus.filter((stop) => stop.kind === 'done').length } of { bonus.length }
                            </Box>
                        </Stack>
                        <Box aria-hidden="true" sx={ { display: 'flex', height: 8, borderRadius: '4px', overflow: 'hidden', bgcolor: '#EDF0F3' } }>
                            <Box sx={ { width: share(finished), bgcolor: '#229A16' } }/>
                            <Box sx={ { width: share(started), bgcolor: '#FFC107' } }/>
                        </Box>
                    </Stack>
                </Stack>

                <NextStep stops={ stops } onStart={ onStart }/>
            </Stack>
        </Stack>
    );
}

type NextStepProps = Pick<CourseMetroHeaderProps, 'stops' | 'onStart'>;

/** The robot's advice: the suggested lesson, what it unlocks, and how many recaps are waiting. */
function NextStep({ stops, onStart }: NextStepProps) {
    const current = stops.find((stop) => stop.kind === 'current');
    const recaps = stops.filter((stop) => stop.kind === 'recap');
    const target = current ?? recaps[0];
    const opens = current
        ? stops.filter((stop) => stop.kind === 'locked' && stop.lesson.previous.includes(current.lesson.id)).slice(0, 2)
        : [];

    return (
        <Stack spacing={ 1.25 } sx={ { width: { xs: '100%', md: 300 }, flexShrink: 0, bgcolor: '#F3F7FE', borderRadius: '14px', p: 1.75, boxSizing: 'border-box' } }>
            <Stack direction="row" spacing={ 1.25 } alignItems="flex-start">
                <KataRobot/>
                <Box sx={ { bgcolor: '#FFFFFF', border: '1px solid #D5E2F7', borderRadius: '12px', px: 1.25, py: 1, fontSize: 13, lineHeight: '18px' } }>
                    { advice(current, recaps.length, opens) }
                </Box>
            </Stack>

            { target && (
                <Button variant="contained" size="large" onClick={ () => onStart(target) }
                        sx={ { borderRadius: '12px', fontWeight: 800, boxShadow: '0 4px 0 #103996' } }>
                    { current ? `Start ${ current.lesson.title }` : `Recap ${ target.lesson.title }` }
                </Button>
            ) }
        </Stack>
    );
}

function advice(current: MetroStop | undefined, recaps: number, opens: MetroStop[]) {
    const recapNote = recaps ? ` ${ recaps } ${ recaps === 1 ? 'recap is' : 'recaps are' } due on the way.` : '';

    if (!current && !recaps) return 'Nothing left to start here. Well done!';
    if (!current) return `Every lesson you can take is started.${ recapNote }`;

    const tail = opens.length ? ` to open ${ opens.map((stop) => stop.lesson.title).join(' and ') }.` : ' to keep the line moving.';
    return <>Finish <strong>{ current.lesson.title }</strong>{ tail }{ recapNote }</>;
}
